import React from 'react';
import styled from 'styled-components';
import { Row, Col, Typography } from 'antd';
import { 
    QrcodeOutlined, 
    PlayCircleOutlined, 
    ClockCircleOutlined, 
    CheckCircleOutlined, 
    EyeOutlined, 
    VideoCameraOutlined, 
    AudioOutlined, 
    SendOutlined,
    LineChartOutlined,
    MessageOutlined,
    BulbOutlined,
    LikeOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    UserOutlined,
    FileTextOutlined,
} from '@ant-design/icons';
import { Radar, Line, Column } from '@ant-design/charts';

const { Title, Text } = Typography;

// --- Styled Components ---

const ReportContainer = styled.div`
    width: 100%;
    background-color: white;
    
    /* Ensure styles are captured correctly by html2canvas */
    * {
        box-sizing: border-box;
    }
`;

const Page = styled.div`
    width: 100%; /* Will be set to specific pixel width for PDF generation */
    height: 100%; /* Will be set to specific pixel height for PDF generation */
    background: white;
    position: relative;
    overflow: hidden;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    
    &.portrait {
        width: 794px; /* A4 Portrait at 96dpi approx */
        height: 1123px;
        padding: ${props => props.$noPadding ? '0' : '40px'};
    }
    
    &.landscape {
        width: 1123px; /* A4 Landscape at 96dpi approx */
        height: 794px;
        padding: ${props => props.$noPadding ? '0' : '40px'};
    }
`;

// --- Cover Page Components ---

const CoverLogo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 120px;
    
    img {
        height: 48px;
    }
    
    span {
        font-size: 32px;
        font-weight: 600;
        color: #4A4A4A;
    }
`;

const CoverDate = styled.div`
    position: absolute;
    top: 50px;
    right: 50px;
    font-size: 16px;
    font-weight: 600;
    color: #000;
`;

const CoverTitle = styled.h1`
    font-size: 64px;
    font-weight: 700;
    color: #8a3ef4;
    line-height: 1.1;
    margin-bottom: 16px;
    max-width: 70%;
`;

const CoverSubtitle = styled.h2`
    font-size: 24px;
    font-weight: 400;
    color: #000;
    margin-bottom: 80px;
`;

const PublishedBy = styled.div`
    margin-bottom: 60px;
    
    .label {
        font-size: 14px;
        letter-spacing: 1px;
        text-transform: uppercase;
        color: #666;
        margin-bottom: 8px;
        font-weight: 600;
    }
    
    .name {
        font-size: 18px;
        font-weight: 700;
        color: #000;
    }
`;

const GeometricPattern = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 45%;
    z-index: 1;
    
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

// --- TOC Components ---

const TocContainer = styled.div`
    display: flex;
    height: 100%;
`;

const TocContent = styled.div`
    flex: 1;
    padding-right: 40px;
`;

const TocSidebar = styled.div`
    width: 150px;
    height: 120%; /* Extend to cover margins if needed, though Page padding handles it */
    background-color: #8a3ef4;
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    
    /* Add geometric pattern */
    // background-image: linear-gradient(135deg, #9b5bf5 25%, transparent 25%), 
    //                   linear-gradient(225deg, #9b5bf5 25%, transparent 25%), 
    //                   linear-gradient(45deg, #9b5bf5 25%, transparent 25%), 
    //                   linear-gradient(315deg, #9b5bf5 25%, transparent 25%);
    // background-position:  10px 0, 10px 0, 0 0, 0 0;
    // background-size: 20px 20px;
    // background-repeat: repeat;
    // opacity: 0.9;
`;

const TocHeader = styled.h2`
    font-size: 48px;
    font-weight: 700;
    color: #000;
    margin-bottom: 60px;
    margin-top: 40px;
`;

const TocItem = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 30px;
    border-bottom: 1px solid #eee;
    padding-bottom: 15px;
    
    .number {
        font-size: 24px;
        font-weight: 700;
        color: #8a3ef4;
        width: 60px;
    }
    
    .text {
        font-size: 18px;
        font-weight: 500;
        color: #333;
    }
`;

// --- Table Components ---

const TableHeader = styled.div`
    background-color: white;
    color: #333;
    padding: 15px 20px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 8px 8px 0 0;
    border: 1px solid #e2e8f0;
    border-bottom: none;
    display: flex;
    justify-content: space-between;
`;

const CustomTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #e2e8f0;
    
    th {
        background-color: #f9f9f9;
        color: #333;
        text-align: left;
        padding: 12px;
        font-weight: 600;
        font-size: 12px;
        white-space: nowrap;
        border-bottom: 1px solid #e2e8f0;
    }
    
    td {
        padding: 16px;
        border-bottom: 1px solid #e2e8f0;
        color: #333;
        background-color: white;
        font-size: 12px;
        white-space: nowrap;
    }
    
    tr:last-child td {
        border-bottom: none;
    }
`;

// --- Dashboard & Survey Styles Reuse ---
// We'll reuse the styled components from AnalyticsPage by exporting them or duplicating small parts for isolation.
// For simplicity and speed, I will duplicate relevant styles here to ensure the PDF looks exactly as needed without affecting the main page.

const StatCardWrapper = styled.div`
    border-radius: 10px;
    border: 1px solid #f0f0f0;
    box-shadow: 0 1px 4px rgba(0,0,0,0.01);
    padding: 8px 10px;
    background: white;
    height: 100%;
    min-height: 80px;
    
    .stat-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 4px;
    }
    
    .stat-label {
        color: #64748b;
        font-size: 10px;
        font-weight: 600;
        line-height: 1.3;
        max-width: 85%;
        text-transform: capitalize;
    }
    
    .stat-icon {
        font-size: 12px;
        color: #8a3ef4;
    }
    
    .stat-value {
        font-size: 16px;
        font-weight: 700;
        margin-bottom: 2px;
        color: #0f172a;
        line-height: 1.2;
    }
    
    .stat-trend {
        font-size: 9px;
        display: flex;
        align-items: center;
        gap: 3px;
        font-weight: 500;
    }
`;

// --- Page 1 Summary Components ---

const SummaryHeader = styled.div`
    text-align: center;
    margin-bottom: 30px;
    
    .brand {
        color: #8a3ef4;
        font-size: 24px;
        font-weight: 700;
        margin-bottom: 8px;
    }
    
    .report-title {
        color: #000;
        font-size: 28px;
        font-weight: 700;
        margin-bottom: 12px;
    }
    
    .meta-info {
        color: #64748b;
        font-size: 12px;
        border-bottom: 1px solid #8a3ef4;
        padding-bottom: 15px;
        display: inline-block;
        width: 100%;
    }
    
    .view-type-label {
        color: #10b981;
        font-size: 16px;
        font-weight: 600;
        margin-top: 12px;
    }
    
    .view-type-label.clinical {
        color: #8a3ef4;
    }
`;

const GroupLeadCard = styled.div`
    background: linear-gradient(135deg, #8a3ef4 0%, #6366f1 100%);
    border-radius: 12px;
    padding: 20px 24px;
    margin-bottom: 20px;
    color: white;
    
    .card-title {
        font-size: 20px;
        font-weight: 700;
        margin-bottom: 4px;
    }
    
    .card-subtitle {
        font-size: 14px;
        opacity: 0.9;
    }
`;

const CCMCoordinatorBanner = styled.div`
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-radius: 12px;
    padding: 24px 28px;
    margin-bottom: 20px;
    color: white;
    
    .banner-title {
        font-size: 22px;
        font-weight: 700;
        margin-bottom: 6px;
    }
    
    .banner-subtitle {
        font-size: 14px;
        opacity: 0.95;
        line-height: 1.5;
    }
`;

const SummarySectionTitle = styled.div`
    margin: 25px 0 10px 0;
    
    .title {
        color: ${props => props.$isCCM ? '#10b981' : '#8a3ef4'};
        font-size: 20px;
        font-weight: 700;
        margin-bottom: 5px;
    }
    
    .description {
        color: #64748b;
        font-size: 12px;
        border-bottom: 1px solid #eee;
        padding-bottom: 10px;
    }
`;

const SummaryCardRow = styled.div`
    display: flex;
    gap: 15px;
    margin-bottom: 15px;
    flex-wrap: nowrap;
`;

const SummaryCard = styled.div`
    flex: 1;
    border-radius: 12px;
    padding: 20px 15px;
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 130px;
    color: white;
    
    &.gradient-purple {
        background: linear-gradient(135deg, #6b84ff 0%, #8a3ef4 100%);
    }
    
    &.gradient-blue {
        background: linear-gradient(135deg, #6b84ff 0%, #4b58ff 100%);
    }
    
    &.solid-purple {
        background-color: #5c67f2;
    }
    
    &.solid-green {
        background-color: #10b981;
    }
    
    &.grey-card {
        background-color: #f1f5f9;
        color: #0f172a;
        min-height: 100px;
    }
    
    .value {
        font-size: 28px;
        font-weight: 700;
        margin-bottom: 5px;
    }
    
    .label {
        font-size: 11px;
        font-weight: 600;
        opacity: 0.9;
        margin-bottom: 8px;
    }
    
    .trend {
        font-size: 10px;
        font-weight: 500;
        opacity: 0.8;
    }
    
    &.grey-card .value {
        font-size: 24px;
        color: ${props => props.$color || '#8a3ef4'};
    }
    
    &.grey-card .label {
        color: #64748b;
        opacity: 1;
        margin-bottom: 12px;
    }
`;

const FunnelContainer = styled.div`
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    margin-top: 25px;
    
    .funnel-title {
        font-size: 16px;
        font-weight: 600;
        color: #334155;
        margin-bottom: 30px;
    }
    
    .funnel-bars {
        display: flex;
        align-items: flex-end;
        justify-content: space-around;
        height: 200px;
        gap: 15px;
        padding-bottom: 20px;
    }
    
    .funnel-item {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        height: 100%;
        justify-content: flex-end;
    }
    
    .bar-container {
        width: 80%;
        background-color: #8a3ef4;
        border-radius: 4px;
        position: relative;
        display: flex;
        justify-content: center;
        min-width: 40px;
        
        .bar-value {
            position: absolute;
            top: -25px;
            font-size: 14px;
            font-weight: 600;
            color: #334155;
            width: 100%;
            text-align: center;
        }
    }
    
    .bar-label {
        margin-top: 10px;
        font-size: 10px;
        font-weight: 600;
        color: #64748b;
        text-align: center;
    }
`;

const TrendsChartContainer = styled.div`
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    margin-top: 25px;
    
    .trends-title {
        font-size: 16px;
        font-weight: 600;
        color: #334155;
        margin-bottom: 20px;
    }
`;

const CCMActivityContainer = styled.div`
    margin-top: 40px;
    
    .section-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 12px;
        
        .section-icon {
            font-size: 20px;
            color: #10b981;
        }
        
        h4 {
            margin: 0;
            font-size: 20px;
            font-weight: 700;
            color: #000;
        }
    }
    
    .section-description {
        color: #64748b;
        font-size: 13px;
        margin-bottom: 24px;
    }
    
    .activity-table {
        width: 100%;
        border-collapse: collapse;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        overflow: hidden;
        
        thead {
            background-color: #f9fafb;
            
            tr {
                border-bottom: 2px solid #e2e8f0;
            }
            
            th {
                padding: 12px 16px;
                text-align: left;
                font-size: 11px;
                font-weight: 600;
                color: #64748b;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                
                &.center {
                    text-align: center;
                }
            }
        }
        
        tbody {
            tr {
                border-bottom: 1px solid #e2e8f0;
                
                &:last-child {
                    border-bottom: none;
                }
                
                &.total-row {
                    background-color: #f9fafb;
                    border-top: 2px solid #e2e8f0;
                    font-weight: 700;
                }
            }
            
            td {
                padding: 14px 16px;
                font-size: 13px;
                color: #334155;
                
                &.name-cell {
                    font-weight: 600;
                    color: #000;
                }
                
                &.tag-cell {
                    .tag {
                        display: inline-block;
                        padding: 4px 12px;
                        border-radius: 20px;
                        background: #f1f5f9;
                        border: 1px solid #e2e8f0;
                        font-size: 11px;
                        font-weight: 600;
                        color: #64748b;
                    }
                }
                
                &.center {
                    text-align: center;
                }
                
                &.videos-cell {
                    font-weight: 700;
                    color: #8a3ef4;
                    text-align: center;
                }
                
                &.enrolled-cell {
                    font-weight: 700;
                    color: #10b981;
                    text-align: center;
                }
                
                &.numeric-cell {
                    font-weight: 600;
                    text-align: center;
                }
            }
        }
    }
`;

const GroupedChartContainer = styled.div`
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    margin-top: 25px;
    
    .chart-title {
        font-size: 16px;
        font-weight: 600;
        color: #334155;
        margin-bottom: 20px;
    }
`;

const SatisfactionCardContainer = styled.div`
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 24px;
    margin-top: 25px;
    background: white;
    position: relative;
    
    .satisfaction-title {
        font-size: 18px;
        font-weight: 600;
        color: #0f172a;
        margin-bottom: 10px;
    }
    
    .overall-score-container {
        text-align: center;
        margin-bottom: 0px;
        font-size: 14px;
        color: #64748b;
    }
    
    .overall-score-value {
        font-weight: 700;
        color: #8a3ef4;
    }
    
    .radar-container {
         height: 300px;
         width: 100%;
         display: flex;
         justify-content: center;
         align-items: center;
     }
`;

const TopVideosContainer = styled.div`
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 24px;
    margin-top: 25px;
    background: white;
    
    .table-title {
        font-size: 18px;
        font-weight: 700;
        color: #0f172a;
        margin-bottom: 20px;
    }
`;

const TopSchedulersContainer = styled.div`
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 24px;
    margin-top: 25px;
    background: white;
    
    .table-title {
        font-size: 18px;
        font-weight: 700;
        color: #0f172a;
        margin-bottom: 20px;
    }
`;

const ReportFooter = styled.div`
    margin-top: auto;
    padding-top: 40px;
    padding-bottom: 20px;
    border-top: 1px solid #e2e8f0;
    text-align: center;
    width: 100%;
    flex-shrink: 0;
    
    .footer-title {
        font-size: 18px;
        font-weight: 700;
        color: #475569;
        margin-bottom: 15px;
    }
    
    .footer-text {
        font-size: 14px;
        color: #64748b;
        margin-bottom: 8px;
    }
    
    .footer-copyright {
        margin-top: 25px;
        font-size: 12px;
        color: #94a3b8;
    }
`;

const BlueTableHeader = styled.table`
    width: 100%;
    border-collapse: collapse;
    
    th {
        background-color: #fff;
        color: black;
        text-align: left;
        padding: 8px 16px;
        font-weight: 600;
        font-size: 13px;
        border: 1px solid #fff;
    }
    
    td {
        padding: 16px;
        border-bottom: 1px solid #e2e8f0;
        color: #334155;
        font-size: 13px;
    }
    
    tr:last-child td {
        border-bottom: none;
    }

    .video-name {
        color: #334155;
        font-weight: 500;
    }
    
    .category-cell {
        color: #64748b;
    }
    
    .views-cell {
        color: #334155;
        text-align: center;
    }
    
    .rating-cell {
        color: #334155;
        text-align: right;
        font-weight: 600;
    }
`;

const AnalyticsReport = ({ analyticsData, dashboardAnalytics, statsData, radarConfig, monthlyComparison, engagementTrend, clinicalVideos, topSchedulers, viewType, selectedGroup, selectedPhysician, ccmManagerActivity, ccmGroupPerformance }) => {
    const now = new Date();
    const currentDate = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const currentTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    
    // Calculate Period (Previous Month)
    const periodDate = new Date();
    periodDate.setMonth(periodDate.getMonth() - 1);
    const periodLabel = periodDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const getReportTitle = () => {
        if (viewType === 'ccm_coordinator') return 'CCM Coordinator Report';
        if (viewType === 'ccm_manager') return 'CCM Manager Report';
        if (viewType === 'ccm_module') return 'CCM Analytics Report';
        if (viewType === 'clinical_lead') return 'Clinical Group Report - All Groups';
        if (viewType === 'physician_view' && selectedPhysician) {
            return `${selectedPhysician.name} - Clinical Report`;
        }
        return 'Clinical Analytics Report';
    };
    
    const getSectionTitle = () => {
        if (viewType.includes('ccm')) return 'CCM Analytics (Chronic Care Management)';
        return 'Clinical Analytics';
    };

    const getSectionDescription = () => {
        if (viewType.includes('ccm')) return 'Video Sent, Video Clicked, Video Total Time, Patient Survey';
        return 'Video Sent, Videos Clicked, Watch Time, Video Completed, Patient Survey';
    };

    const getTrendsTitle = () => {
        if (viewType.includes('ccm')) return 'CCM 6-Month Trends';
        return 'Clinical 6-Month Trends';
    };

    // Patient Data (Dynamic with Fallback)
    const patientData = (analyticsData?.monthlyView?.patients && Array.isArray(analyticsData.monthlyView.patients)) 
        ? analyticsData.monthlyView.patients.map(p => ({
            id: 'S. No.', // Placeholder, will use index
            name: p.patientName || p.name || 'Unknown',
            phone: p.phone ? (p.phone.length > 4 ? `${p.phone.slice(-4)}` : p.phone) : '----',
            email: p.email 
                ? (p.email.includes('@') 
                    ? `${p.email.charAt(0)}****${p.email.substring(p.email.indexOf('@'))}` 
                    : p.email) 
                : '----',
            videos: p.videosSent || p.videos || 0,
            minutes: p.totalVideoMinutes || p.minutes || 0
        }))
        : [
            { id: '1', name: 'John Smith', phone: '3321', email: 'j***@gmail.com', videos: 6, minutes: 72 },
            { id: '2', name: 'John Smith', phone: '9144', email: 'j***@yahoo.com', videos: 4, minutes: 48 },
            { id: '3', name: 'Maria Lopez', phone: '1180', email: 'm***@icloud.com', videos: 3, minutes: 36 },
            { id: '4', name: 'Robert Doe', phone: '5562', email: 'r***@gmail.com', videos: 8, minutes: 95 },
            { id: '5', name: 'Emily White', phone: '7789', email: 'e***@hotmail.com', videos: 2, minutes: 15 },
        ];


    // Generate Mock Data for Charts based on current stats to make it semi-dynamic
    // 1. Engagement Trends (Last 6 Months)
    const getEngagementTrendsData = () => {
        if (engagementTrend && Array.isArray(engagementTrend) && engagementTrend.length > 0) {
            const data = [];
            engagementTrend.forEach((item) => {
                const date = new Date(item.month);
                const monthLabel = date.toLocaleDateString('en-US', { month: 'short' });
                
                data.push({ month: monthLabel, value: item.videosSent || 0, type: 'Sent' });
                data.push({ month: monthLabel, value: item.videosWatched || 0, type: 'Clicked' });
                data.push({ month: monthLabel, value: item.surveys || Math.floor((item.videosWatched || 0) * 0.4), type: 'Surveys' });
            });
            return data;
        }

        const months = ['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'];
        const data = [];
        
        months.forEach((month, index) => {
            // Create some variation curve matching images
            const base = 50 + Math.sin(index) * 10;
            
            data.push({ month, value: Math.floor(base * 1.5), type: 'Sent' });
            data.push({ month, value: Math.floor(base * 1.2), type: 'Clicked' });
            data.push({ month, value: Math.floor(base * 0.8), type: 'Surveys' });
        });
        return data;
    };

    const trendsConfig = {
        data: getEngagementTrendsData(),
        xField: 'month',
        yField: 'value',
        seriesField: 'type',
        smooth: true,
        animation: false,
        legend: false,
        color: ['#8a3ef4', '#3b82f6', '#10b981'],
        point: {
            size: 4,
            shape: 'circle',
            style: {
                fill: 'white',
                stroke: (datum) => {
                    if (datum.type === 'Sent') return '#8a3ef4';
                    if (datum.type === 'Clicked') return '#3b82f6';
                    return '#10b981';
                },
                lineWidth: 2,
            },
        },
        lineStyle: {
            lineWidth: 2,
        },
        xAxis: {
            label: {
                style: {
                    fontSize: 10,
                    fill: '#64748b',
                },
            },
            line: null,
            tickLine: null,
        },
        yAxis: false,
    };

    // 1b. Videos Sent & Enrollment by Group (CCM Only)
    const getGroupEnrollmentData = () => {
        // Use real data if available in future, currently static as requested
        return [
            { group: 'Cardiac Care...', type: 'Videos Sent', value: 142 },
            { group: 'Cardiac Care...', type: 'Patients Enrolled', value: 48 },
            { group: 'Endocrine Sp...', type: 'Videos Sent', value: 118 },
            { group: 'Endocrine Sp...', type: 'Patients Enrolled', value: 38 },
            { group: 'Pulmonary Pa...', type: 'Videos Sent', value: 96 },
            { group: 'Pulmonary Pa...', type: 'Patients Enrolled', value: 32 },
            { group: 'Renal Health...', type: 'Videos Sent', value: 84 },
            { group: 'Renal Health...', type: 'Patients Enrolled', value: 26 },
            { group: 'Rheumatology...', type: 'Videos Sent', value: 68 },
            { group: 'Rheumatology...', type: 'Patients Enrolled', value: 22 },
            { group: 'Senior Care ...', type: 'Videos Sent', value: 134 },
            { group: 'Senior Care ...', type: 'Patients Enrolled', value: 44 },
        ];
    };

    const groupEnrollmentConfig = {
        data: getGroupEnrollmentData(),
        isGroup: true,
        xField: 'group',
        yField: 'value',
        seriesField: 'type',
        animation: false,
        color: ['#8a3ef4', '#10b981'],
        legend: {
            position: 'top-left',
            offsetY: -10,
        },
        label: {
            position: 'top',
            offsetY: 8,
            style: {
                fontSize: 10,
                fontWeight: 600,
                fill: '#334155',
            },
        },
        xAxis: {
            label: {
                style: {
                    fontSize: 9,
                    fill: '#64748b',
                },
            },
        },
        yAxis: false,
    };

    // 2. Monthly Comparison (Current vs Previous)
    const getComparisonData = () => {
        if (monthlyComparison) {
            // Helper to extract object whether it's direct or in array
            const extractData = (data) => {
                if (Array.isArray(data)) return data[0] || {};
                if (typeof data === 'object' && data !== null) return data;
                return {};
            };

            const current = extractData(monthlyComparison.currentMonth);
            const previous = extractData(monthlyComparison.previousMonth);
            
            return [
                { name: 'Videos Sent', type: 'Current Month', value: current.videosSent || current.VideosSent || 0 },
                { name: 'Videos Sent', type: 'Previous Month', value: previous.videosSent || previous.VideosSent || 0 },
                { name: 'Videos Watched', type: 'Current Month', value: current.videosWatched || current.VideosWatched || 0 },
                { name: 'Videos Watched', type: 'Previous Month', value: previous.videosWatched || previous.VideosWatched || 0 },
            ];
        }

        const metrics = [
            { key: 'Videos Watched', current: 1700, prev: 1500 }, // +12%
            { key: 'Videos Sent', current: 166, prev: 50 }, // +231%
        ];
        
        const data = [];
        metrics.forEach(m => {
            data.push({ name: m.key, type: 'Current Month', value: m.current });
            data.push({ name: m.key, type: 'Previous Month', value: m.prev });
        });
        return data;
    };

    const comparisonConfig = {
        data: getComparisonData(),
        isGroup: true,
        xField: 'name',
        yField: 'value',
        seriesField: 'type',
        animation: false,
        color: ['#8a3ef4', '#52c41a'],
        legend: { position: 'bottom' },
        label: {
            position: 'top',
            offsetY: 9,
            style: {
                fill: '#000',
                fontSize: 12,
                fontWeight: 'semibold',
                opacity: 1,
            },
            formatter: (v) => {
                const val = parseFloat(v.value);
                return val >= 1000 ? (val / 1000).toFixed(1) + 'K' : val;
            }
        },
    };

    // 3. Survey Questions Data (Dynamic with Fallback)
    const getSurveyQuestions = () => {
        if (analyticsData?.perQuestion && Array.isArray(analyticsData.perQuestion)) {
            return analyticsData.perQuestion.map((survey) => {
                const getQuestionConfig = (qNum) => {
                    switch (qNum) {
                        case 1:
                            return {
                                icon: <BulbOutlined />,
                                labels: ['Very Confident', 'Confident', 'Somewhat Confident', 'Not Very Confident', 'Not Confident at All']
                            };
                        case 2:
                            return {
                                icon: <LikeOutlined />,
                                labels: ['Much More Helpful', 'More Helpful', 'About the Same', 'Somewhat Less Helpful', 'Less Helpful']
                            };
                        case 3:
                            return {
                                icon: <CheckCircleOutlined />,
                                labels: ['Yes, Definitely', 'Somewhat', 'Not at All']
                            };
                        case 4:
                            return {
                                icon: <PlayCircleOutlined />,
                                labels: ['Significantly Reduced', 'Somewhat Reduced', 'No Change', 'Slightly Increased', 'Increased Anxiety']
                            };
                        case 5:
                            return {
                                icon: <MessageOutlined />,
                                labels: ['Yes', 'Not Sure', 'No']
                            };
                        default:
                            return {
                                icon: <MessageOutlined />,
                                labels: ['Option 1', 'Option 2', 'Option 3', 'Option 4', 'Option 5']
                            };
                    }
                };

                const config = getQuestionConfig(survey.questionNumber);
                const scoreVal = parseFloat(survey.averageRating || 0);
                const color = scoreVal < 4 ? '#faad14' : '#8a3ef4';
                
                // Map options
                const formattedOptions = survey.options
                    ? survey.options
                        .filter(opt => opt.index <= config.labels.length)
                        .map(opt => ({
                            label: config.labels[opt.index - 1],
                            count: opt.count,
                            percent: opt.percentage
                        }))
                    : [];

                return {
                    id: survey.questionNumber,
                    icon: config.icon,
                    question: survey.question,
                    score: scoreVal.toFixed(2),
                    responses: survey.totalResponses,
                    color: color,
                    options: formattedOptions
                };
            });
        }

        return [
            {
                id: 1,
                icon: <CheckCircleOutlined />,
                question: "After watching the video, how confident do you feel in understanding your upcoming procedure?",
                score: 4.65,
                responses: 292,
                color: '#8a3ef4',
                options: [
                    { label: "Very Confident", count: 124, percent: 42.5 },
                    { label: "Confident", count: 103, percent: 35.3 },
                    { label: "Somewhat Confident", count: 34, percent: 11.6 },
                    { label: "Not Very Confident", count: 19, percent: 6.5 },
                    { label: "Not Confident at All", count: 12, percent: 4.1 }
                ]
            },
            {
                id: 2,
                icon: <LikeOutlined />,
                question: "How helpful was the video in preparing you compared to written instructions?",
                score: 3.80,
                responses: 256,
                color: '#faad14',
                options: [
                    { label: "Much More Helpful", count: 79, percent: 30.9 },
                    { label: "More Helpful", count: 84, percent: 32.8 },
                    { label: "About the Same", count: 66, percent: 25.8 },
                    { label: "Less Helpful", count: 16, percent: 6.3 },
                    { label: "Not Helpful", count: 11, percent: 4.3 }
                ]
            },
            {
                id: 3,
                icon: <VideoCameraOutlined />,
                question: "How would you rate the audio and visual quality of the video content provided?",
                score: 4.85,
                responses: 288,
                color: '#8a3ef4',
                options: [
                    { label: "Excellent", count: 200, percent: 69.4 },
                    { label: "Good", count: 60, percent: 20.8 },
                    { label: "Average", count: 20, percent: 6.9 },
                    { label: "Poor", count: 5, percent: 1.7 },
                    { label: "Very Poor", count: 3, percent: 1.0 }
                ]
            },
            {
                id: 4,
                icon: <MessageOutlined />,
                question: "Did the video content answer your specific questions regarding your condition?",
                score: 4.20,
                responses: 275,
                color: '#8a3ef4',
                options: [
                    { label: "Completely", count: 150, percent: 54.5 },
                    { label: "Mostly", count: 80, percent: 29.1 },
                    { label: "Somewhat", count: 30, percent: 10.9 },
                    { label: "Barely", count: 10, percent: 3.6 },
                    { label: "Not at all", count: 5, percent: 1.8 }
                ]
            }
        ];
    };

    const surveyQuestions = getSurveyQuestions();

    // Helper for funnel bars
    const videosSent = dashboardAnalytics?.totalVideoSent || 0;
    const videosClicked = dashboardAnalytics?.totalClicks || 0;
    const videosWatched = dashboardAnalytics?.totalViews || 0; // Assuming views = completed for now
    const surveyResponses = analyticsData?.totalResponses || 0;
    
    const completionRate = videosClicked > 0 
        ? ((videosWatched / videosClicked) * 100).toFixed(1) 
        : "0.0";

    const funnelData = [
        { label: 'Videos Sent', value: videosSent },
        { label: 'Clicked', value: videosClicked },
        { label: 'Completed', value: videosWatched },
        { label: 'Surveys', value: surveyResponses },
    ];
    
    const maxFunnelValue = Math.max(...funnelData.map(d => d.value), 1);

    return (
        <ReportContainer id="analytics-report-container">
            
            {/* Page 1: Summary Dashboard */}
            <Page className="report-page portrait" id="report-page-1">
                <SummaryHeader>
                    <div className="brand">Meducate Analytics</div>
                    <div className="report-title">{getReportTitle()}</div>
                    <div className="meta-info">
                        Generated on {currentDate} at {currentTime} | Period: {periodLabel}
                    </div>
                    {viewType === 'ccm_coordinator' && (
                        <div className="view-type-label">CCM Coordinator View</div>
                    )}
                    {viewType === 'clinical_lead' && (
                        <div className="view-type-label clinical">Clinical Group Lead View</div>
                    )}
                </SummaryHeader>

                {viewType === 'ccm_coordinator' && (
                    <CCMCoordinatorBanner>
                        <div className="banner-title">CCM Coordinator Report</div>
                        <div className="banner-subtitle">
                            Comprehensive view of all CCM managers, patient enrollment, and program metrics
                        </div>
                    </CCMCoordinatorBanner>
                )}

                {viewType === 'clinical_lead' && selectedGroup && (
                    <GroupLeadCard>
                        <div className="card-title">Clinical Group Lead Report</div>
                        <div className="card-subtitle">
                            {selectedGroup.hospitalName} - {selectedGroup.specialty?.[0] || 'General'} | {selectedGroup.totalPhysician?.length || 0} Physicians
                        </div>
                    </GroupLeadCard>
                )}

                {viewType === 'physician_view' && selectedPhysician && (
                    <GroupLeadCard>
                        <div className="card-title">Physician Analytics Report</div>
                        <div className="card-subtitle">
                            {selectedPhysician.name} - {selectedPhysician.specialty || 'General'} | {selectedPhysician.hospitalName || 'General Hospital'}
                        </div>
                    </GroupLeadCard>
                )}

                <SummarySectionTitle $isCCM={viewType.includes('ccm')}>
                    <div className="title">{getSectionTitle()}</div>
                    <div className="description">
                        {getSectionDescription()}
                    </div>
                </SummarySectionTitle>

                <SummaryCardRow>
                    <SummaryCard className="gradient-purple">
                        <div className="value">{videosSent}</div>
                        <div className="label">Videos Sent</div>
                        <div className="trend">-26% vs last month</div>
                    </SummaryCard>
                    <SummaryCard className="gradient-blue">
                        <div className="value">{videosClicked}</div>
                        <div className="label">Videos Clicked</div>
                        <div className="trend">-20% vs last month</div>
                    </SummaryCard>
                    <SummaryCard className="solid-purple">
                        <div className="value">{dashboardAnalytics?.totalWatchTime || '0:00'}</div>
                        <div className="label">{viewType.includes('ccm') ? 'Total Video Time' : 'Watch Time'}</div>
                    </SummaryCard>
                    <SummaryCard className="solid-green">
                        <div className="value">{viewType.includes('ccm') ? (analyticsData?.totalEnrolled || 15) : videosWatched}</div>
                        <div className="label">{viewType.includes('ccm') ? 'Patients Enrolled' : 'Videos Completed'}</div>
                        <div className="trend">{viewType.includes('ccm') ? '-62% vs last month' : '-1% vs last month'}</div>
                    </SummaryCard>
                </SummaryCardRow>

                <SummaryCardRow>
                    <SummaryCard className="grey-card" $color="#8a3ef4">
                        <div className="label">{viewType.includes('ccm') ? 'Total Plans' : 'Completion Rate'}</div>
                        <div className="value">{viewType.includes('ccm') ? (analyticsData?.totalPlans || 19) : `${completionRate}%`}</div>
                    </SummaryCard>
                    <SummaryCard className="grey-card" $color="#3b82f6">
                        <div className="label">{viewType.includes('ccm') ? 'Avg Min/Patient' : 'Avg Survey Score'}</div>
                        <div className="value">
                            {viewType.includes('ccm') 
                                ? (analyticsData?.avgMinPerPatient || 27) 
                                : `${analyticsData?.overallAverage || '0.00'} / 5.0`}
                        </div>
                    </SummaryCard>
                    <SummaryCard className="grey-card" $color="#faad14">
                        <div className="label">Survey Responses</div>
                        <div className="value">{surveyResponses}</div>
                        <div className="trend">-69% vs last month</div>
                    </SummaryCard>
                    {viewType.includes('ccm') && (
                         <SummaryCard className="grey-card" $color="#10b981">
                             <div className="label">Avg Survey Score</div>
                             <div className="value">{analyticsData?.overallAverage || '3.4'}</div>
                         </SummaryCard>
                     )}
                 </SummaryCardRow>

                 {viewType.includes('ccm') && (
                    <div style={{ height: '20px' }} />
                 )}

                 <SatisfactionCardContainer>
                    <div className="satisfaction-title">Patient Satisfaction (Survey Scores)</div>
                    <div className="overall-score-container">
                        Overall Score: <span className="overall-score-value">{analyticsData?.overallAverage || '0.00'} / 5.0</span>
                    </div>
                    <div className="radar-container">
                        <Radar 
                            {...radarConfig} 
                            animation={false}
                            xAxis={{
                                label: {
                                    formatter: (text) => {
                                        const item = radarConfig.data?.find(d => d.item === text);
                                        return `${text}\n${item ? item.score.toFixed(1) : ''}`;
                                    },
                                    style: {
                                        fontSize: 11,
                                        fontWeight: 600,
                                        fill: '#8a3ef4',
                                    }
                                }
                            }}
                        />
                    </div>
                 </SatisfactionCardContainer>
            </Page>

            {/* Page 1c: Top Clinical Videos (Clinical Only) */}
            {!viewType.includes('ccm') && (
                <Page className="report-page portrait" id="report-page-1c">
                    <TopVideosContainer>
                        <div className="table-title">Top Clinical Videos</div>
                        <BlueTableHeader>
                            <thead>
                                <tr>
                                    <th>Video</th>
                                    <th>Category</th>
                                    <th style={{ textAlign: 'center' }}>Views</th>
                                    <th style={{ textAlign: 'right' }}>Rating</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(clinicalVideos && clinicalVideos.length > 0 ? clinicalVideos : [
                                    { videoName: 'Pre-Operative Care Instructions', videoSpecialty: 'Surgery', clickedCount: 707, rating: '3.9' },
                                    { videoName: 'Total Knee Replacement Guide', videoSpecialty: 'Orthopedics', clickedCount: 842, rating: '3.5' },
                                    { videoName: 'Cardiac Catheterization Overview', videoSpecialty: 'Cardiology', clickedCount: 371, rating: '4.6' },
                                    { videoName: 'Colonoscopy Preparation', videoSpecialty: 'GI', clickedCount: 543, rating: '3.8' },
                                    { videoName: 'Post-Surgical Wound Care', videoSpecialty: 'Surgery', clickedCount: 626, rating: '3.8' },
                                    { videoName: 'Physical Therapy Exercises', videoSpecialty: 'PT', clickedCount: 449, rating: '4.7' },
                                    { videoName: 'Diabetes Self-Management', videoSpecialty: 'Endocrinology', clickedCount: 827, rating: '3.9' },
                                    { videoName: 'Blood Pressure Monitoring', videoSpecialty: 'Cardiology', clickedCount: 893, rating: '4.7' },
                                ]).map((video, idx) => (
                                    <tr key={idx}>
                                        <td className="video-name">#{idx + 1} {video.videoName}</td>
                                        <td className="category-cell">{video.videoSpecialty || 'Clinical'}</td>
                                        <td className="views-cell">{video.clickedCount}</td>
                                        <td className="rating-cell">{video.rating || (3 + Math.random()).toFixed(1)} / 5.0</td>
                                    </tr>
                                ))}
                            </tbody>
                        </BlueTableHeader>
                    </TopVideosContainer>
                </Page>
            )}

            {/* Page 1d: Top Schedulers (Clinical Only) */}
            {!viewType.includes('ccm') && (
                <Page className="report-page portrait" id="report-page-1d">
                    <TopSchedulersContainer>
                        <div className="table-title">Top Schedulers Sending Videos</div>
                        <BlueTableHeader>
                            <thead>
                                <tr>
                                    <th style={{ color: '#000', borderRadius: '4px 0 0 0' }}>Scheduler</th>
                                    <th style={{ color: '#000' }}>Email</th>
                                    <th style={{ color: '#000', textAlign: 'center' }}>Videos Sent</th>
                                    <th style={{ color: '#000', textAlign: 'center' }}>Patients</th>
                                    <th style={{ color: '#000', textAlign: 'right', borderRadius: '0 4px 0 0' }}>Response Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(topSchedulers && topSchedulers.length > 0 ? topSchedulers : [
                                    { name: 'Sarah Johnson', email: 'sarah.j@meducate.com', totalVideoSend: 478, patients: 204, responseRate: 84 },
                                    { name: 'Michael Chen', email: 'michael.c@meducate.com', totalVideoSend: 299, patients: 166, responseRate: 87 },
                                    { name: 'Emily Rodriguez', email: 'emily.r@meducate.com', totalVideoSend: 401, patients: 99, responseRate: 62 },
                                    { name: 'David Kim', email: 'david.k@meducate.com', totalVideoSend: 490, patients: 169, responseRate: 70 },
                                    { name: 'Jessica Williams', email: 'jessica.w@meducate.com', totalVideoSend: 380, patients: 164, responseRate: 78 },
                                ]).map((scheduler, idx) => {
                                    const maskEmail = (email) => {
                                        if (!email || !email.includes('@')) return '----';
                                        const [name, domain] = email.split('@');
                                        return `${name.charAt(0)}****@${domain}`;
                                    };
                                    return (
                                        <tr key={idx}>
                                            <td className="video-name">{scheduler.name || scheduler.scheduledBy}</td>
                                            <td className="category-cell">{maskEmail(scheduler.email)}</td>
                                            <td className="views-cell" style={{ textAlign: 'center' }}>{scheduler.totalVideoSend}</td>
                                            <td className="views-cell" style={{ textAlign: 'center' }}>{scheduler.patients}</td>
                                            <td className="rating-cell">{scheduler.responseRate}%</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </BlueTableHeader>
                    </TopSchedulersContainer>

                    {/* Integrated Footer */}
                    <ReportFooter>
                        <div className="footer-title">Meducate Analytics Report</div>
                        <div className="footer-text">This report was automatically generated by the Meducate Analytics System.</div>
                        <div className="footer-text">For questions or support, please contact your system administrator.</div>
                        <div className="footer-copyright">
                            © 2026 Meducate. All rights reserved. | Confidential Information
                        </div>
                    </ReportFooter>
                </Page>
            )}

            {/* Page 1b: Trends Chart (Dedicated page for Clinical/Global or overflow for CCM) */}
            <Page className="report-page portrait" id="report-page-1b">
                {viewType.includes('ccm') ? (
                    <>
                        <TrendsChartContainer style={{ marginTop: '40px' }}>
                            <div className="trends-title">{getTrendsTitle()}</div>
                            <div style={{ height: '350px' }}>
                                <Line {...trendsConfig} />
                            </div>
                        </TrendsChartContainer>
                        <GroupedChartContainer>
                            <div className="chart-title">Videos Sent & Enrollment by Group</div>
                            <div style={{ height: '350px' }}>
                                <Column {...groupEnrollmentConfig} />
                            </div>
                        </GroupedChartContainer>
                    </>
                ) : (
                    <>
                        <TrendsChartContainer style={{ marginTop: '20px' }}>
                            <div className="trends-title">{getTrendsTitle()}</div>
                            <div style={{ height: '350px' }}>
                                <Line {...trendsConfig} />
                            </div>
                        </TrendsChartContainer>

                        <FunnelContainer style={{ marginTop: '30px' }}>
                            <div className="funnel-title">{getSectionTitle()} Engagement Funnel</div>
                            <div className="funnel-bars">
                                {funnelData.map((item, idx) => {
                                    // Calculate height in pixels (out of 160px usable space in 200px container)
                                    const barHeight = Math.max((item.value / maxFunnelValue) * 160, 2);
                                    return (
                                        <div key={idx} className="funnel-item">
                                            <div className="bar-container" style={{ height: `${barHeight}px` }}>
                                                <span className="bar-value">{item.value}</span>
                                            </div>
                                            <div className="bar-label">{item.label}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </FunnelContainer>
                    </>
                )}
            </Page>

            {/* Page 3: CCM Activity by Manager (CCM Only) */}
            {viewType.includes('ccm') && (
                <Page className="report-page portrait" id="report-page-3">
                    <CCMActivityContainer>
                        <div className="section-header">
                            <UserOutlined className="section-icon" />
                            <h4>CCM Activity by Manager</h4>
                        </div>
                        <div className="section-description">
                            Individual CCM manager video and enrollment tracking
                        </div>
                        
                        <table className="activity-table">
                            <thead>
                                <tr>
                                    <th>CCM Manager</th>
                                    <th>Role</th>
                                    <th className="center">Videos Sent</th>
                                    <th className="center">Patients Enrolled</th>
                                    <th className="center">Total Plans</th>
                                    <th className="center">Total Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ccmManagerActivity && ccmManagerActivity.length > 0 ? (
                                    ccmManagerActivity.map((manager, index) => (
                                        <tr key={index}>
                                            <td className="name-cell">
                                                {manager.name ? manager.name.charAt(0).toUpperCase() + manager.name.slice(1) : 'N/A'}
                                            </td>
                                            <td className="tag-cell">
                                                <span className="tag">{manager.role || 'CCM Manager'}</span>
                                            </td>
                                            <td className="videos-cell">{manager.videosSent || 0}</td>
                                            <td className="enrolled-cell">{manager.enrolledPatients || 0}</td>
                                            <td className="numeric-cell">{manager.totalPlans || 0}</td>
                                            <td className="numeric-cell">{manager.totalTime || '0h 0m'}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                                            No CCM manager activity data available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </CCMActivityContainer>
                </Page>
            )}

            {/* Page 4: CCM Activity by Group (CCM Only) */}
            {viewType.includes('ccm') && (
                <Page className="report-page portrait" id="report-page-4">
                    <CCMActivityContainer>
                        <div className="section-header">
                            <FileTextOutlined className="section-icon" />
                            <h4>CCM Activity by Group</h4>
                        </div>
                        <div className="section-description">
                            Group-level CCM video and enrollment summary
                        </div>
                        
                        <table className="activity-table">
                            <thead>
                                <tr>
                                    <th>Group</th>
                                    <th>Specialty</th>
                                    <th className="center">Videos Sent</th>
                                    <th className="center">Patients Enrolled</th>
                                    <th className="center">Total Plans</th>
                                    <th className="center">Total Time</th>
                                    <th className="center">Surveys</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ccmGroupPerformance && ccmGroupPerformance.length > 0 ? (
                                    <>
                                        {ccmGroupPerformance.map((group, index) => (
                                            <tr key={index}>
                                                <td className="name-cell">{group.groupName || 'N/A'}</td>
                                                <td className="tag-cell">
                                                    <span className="tag">{group.specialty || 'N/A'}</span>
                                                </td>
                                                <td className="videos-cell">{group.videosSent || 0}</td>
                                                <td className="enrolled-cell">{group.patientsEnrolled || 0}</td>
                                                <td className="numeric-cell">{group.totalPlans || 0}</td>
                                                <td className="numeric-cell">{group.totalTime || '0h 0m'}</td>
                                                <td className="numeric-cell">{group.surveys || 0}</td>
                                            </tr>
                                        ))}
                                        <tr className="total-row">
                                            <td className="name-cell">Total</td>
                                            <td></td>
                                            <td className="videos-cell">
                                                {ccmGroupPerformance.reduce((sum, g) => sum + (g.videosSent || 0), 0)}
                                            </td>
                                            <td className="enrolled-cell">
                                                {ccmGroupPerformance.reduce((sum, g) => sum + (g.patientsEnrolled || 0), 0)}
                                            </td>
                                            <td className="numeric-cell">
                                                {ccmGroupPerformance.reduce((sum, g) => sum + (g.totalPlans || 0), 0)}
                                            </td>
                                            <td className="numeric-cell">
                                                {(() => {
                                                    const totalMinutes = ccmGroupPerformance.reduce((sum, g) => {
                                                        const time = String(g.totalTime || '0h 0m');
                                                        const match = time.match(/(\d+)h\s*(\d+)m/);
                                                        if (match) {
                                                            return sum + (parseInt(match[1]) * 60) + parseInt(match[2]);
                                                        }
                                                        return sum;
                                                    }, 0);
                                                    const hours = Math.floor(totalMinutes / 60);
                                                    const minutes = totalMinutes % 60;
                                                    return `${hours}h ${minutes}m`;
                                                })()}
                                            </td>
                                            <td className="numeric-cell">
                                                {ccmGroupPerformance.reduce((sum, g) => sum + (g.surveys || 0), 0)}
                                            </td>
                                        </tr>
                                    </>
                                ) : (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                                            No CCM group performance data available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </CCMActivityContainer>

                    {/* Integrated Footer */}
                    <ReportFooter>
                        <div className="footer-title">Meducate Analytics Report</div>
                        <div className="footer-text">This report was automatically generated by the Meducate Analytics System.</div>
                        <div className="footer-text">For questions or support, please contact your system administrator.</div>
                        <div className="footer-copyright">
                            © 2026 Meducate. All rights reserved. | Confidential Information
                        </div>
                    </ReportFooter>
                </Page>
            )}
            
        </ReportContainer>
    );
};

export default AnalyticsReport;
